import { useState } from "react";
import { Card } from "./ui/card";
import {
  Briefcase, ExternalLink, Search, Compass, Building2, BookOpen, Wrench,
} from "lucide-react";

// Curated list. To add more, just append an entry with id/name/url/Icon/desc.
const SITES = [
  {
    id: "cdle",
    name: "Colorado Dept. of Labor",
    short: "CDLE",
    url: "https://cdle.colorado.gov/",
    desc: "State labor info, unemployment, training.",
    Icon: Building2,
    color: "bg-blue-50 text-blue-700",
  },
  {
    id: "connectingco",
    name: "Connecting Colorado",
    short: "Jobs in CO",
    url: "https://www.connectingcolorado.com/",
    desc: "Colorado's state job listings board.",
    Icon: Search,
    color: "bg-emerald-50 text-emerald-700",
  },
  {
    id: "mycoloradojourney",
    name: "MyColoradoJourney",
    short: "Career Plan",
    url: "https://www.mycoloradojourney.com/",
    desc: "Career planning and learning paths.",
    Icon: Compass,
    color: "bg-purple-50 text-purple-700",
  },
  {
    id: "usajobs",
    name: "USAJobs",
    short: "Federal Jobs",
    url: "https://www.usajobs.gov/",
    desc: "Federal government job listings.",
    Icon: Briefcase,
    color: "bg-slate-100 text-slate-700",
  },
  {
    id: "onet",
    name: "O*NET Online",
    short: "Explore Careers",
    url: "https://www.onetonline.org/",
    desc: "Explore jobs, skills, and pay.",
    Icon: Wrench,
    color: "bg-amber-50 text-amber-700",
  },
  {
    id: "bls",
    name: "Occupational Outlook",
    short: "Pay & Outlook",
    url: "https://www.bls.gov/ooh/",
    desc: "Pay, growth, and what each job does.",
    Icon: BookOpen,
    color: "bg-rose-50 text-rose-700",
  },
];

// Free thumbnail service (no API key). Captures the homepage at desktop width
// and we use object-contain below so the whole top of the site fits inside the card.
const thumbFor = (url) => `https://image.thum.io/get/width/1200/noanimate/${url}`;

function SiteCard({ site }) {
  const { Icon } = site;
  const [imgFailed, setImgFailed] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);

  return (
    <a
      href={site.url}
      target="_blank"
      rel="noopener noreferrer"
      className="group text-left rounded-xl border border-slate-200 hover:border-slate-400 hover:-translate-y-0.5 hover:shadow-sm transition overflow-hidden bg-white flex flex-col"
      data-testid={`hub-site-${site.id}`}
      title={`${site.desc} (opens in a new tab)`}
    >
      <div className="relative aspect-[16/10] w-full bg-white border-b border-slate-100 overflow-hidden">
        {!imgFailed && (
          <img
            src={thumbFor(site.url)}
            alt={`${site.name} homepage preview`}
            loading="lazy"
            onLoad={() => setImgLoaded(true)}
            onError={() => setImgFailed(true)}
            className={`absolute inset-0 w-full h-full object-contain object-top transition-opacity duration-300 ${imgLoaded ? "opacity-100" : "opacity-0"}`}
            data-testid={`hub-thumb-${site.id}`}
          />
        )}
        {(imgFailed || !imgLoaded) && (
          <div className={`absolute inset-0 flex items-center justify-center ${site.color}`}>
            <Icon className="w-8 h-8 opacity-80" />
          </div>
        )}
        <div className="absolute top-1.5 left-1.5 inline-flex items-center gap-1 bg-white/90 backdrop-blur px-1.5 py-0.5 rounded-md border border-slate-200">
          <span className={`w-4 h-4 rounded ${site.color} flex items-center justify-center`}>
            <Icon className="w-2.5 h-2.5" />
          </span>
          <span className="text-[10px] font-semibold text-slate-700 tracking-wide">{site.short}</span>
        </div>
        <div className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition bg-slate-900/85 text-white text-[10px] px-1.5 py-0.5 rounded inline-flex items-center gap-1">
          <ExternalLink className="w-2.5 h-2.5" /> Open
        </div>
      </div>
      <div className="p-2.5">
        <div className="font-display font-semibold text-sm text-slate-900 group-hover:text-blue-600 transition line-clamp-1">{site.name}</div>
        <div className="text-[11px] text-slate-500 mt-0.5 leading-tight line-clamp-2">{site.desc}</div>
      </div>
    </a>
  );
}

export default function JobSearchHub() {
  return (
    <Card className="mb-6 p-5" data-testid="job-search-hub">
      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div>
          <div className="font-display font-semibold text-slate-900 flex items-center gap-2">
            <span className="w-7 h-7 rounded-md bg-slate-900 text-white flex items-center justify-center"><Briefcase className="w-3.5 h-3.5" /></span>
            Job search hub
          </div>
          <div className="text-sm text-slate-500 mt-1">
            Click a card to open the site in a new tab.
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-6 gap-3">
        {SITES.map((s) => (
          <SiteCard key={s.id} site={s} />
        ))}
      </div>
    </Card>
  );
}
